import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { fetchPurchaseOrders } from '../../store/poSlice';
import { createInvoice } from '../../store/invoiceSlice';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const CreateInvoice: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { purchaseOrders, loading: loadingPOs } = useSelector((state: RootState) => state.purchaseOrders);
  const { loading, error } = useSelector((state: RootState) => state.invoices);
  const { user } = useSelector((state: RootState) => state.auth);

  const [formData, setFormData] = useState({
    poId: '',
    amount: 0,
    taxAmount: 0,
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    status: 'draft',
  });

  const [selectedPO, setSelectedPO] = useState<any>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Fetch completed/accepted POs
    dispatch(fetchPurchaseOrders({ status: 'accepted' }));
  }, [dispatch]);

  useEffect(() => {
    // Auto-populate invoice when PO is selected
    if (formData.poId) {
      const po = purchaseOrders.find((p: any) => p._id === formData.poId);
      if (po) {
        setSelectedPO(po);
        setFormData(prev => ({
          ...prev,
          amount: po.totalAmount || 0,
          taxAmount: (po.totalAmount || 0) * 0.1, // Default 10% tax
        }));
      }
    } else {
      setSelectedPO(null);
    }
  }, [formData.poId, purchaseOrders]);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: parseFloat(value) || 0 });
  };

  const calculateTotalAmount = () => {
    return formData.amount + formData.taxAmount;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess(false);

    // Validation
    if (!formData.poId) {
      return;
    }

    if (!formData.dueDate) {
      return;
    }

    try {
      const invoiceData = {
        poId: formData.poId,
        supplierId: selectedPO.supplierId._id || selectedPO.supplierId,
        buyerId: selectedPO.buyerId._id || selectedPO.buyerId,
        amount: formData.amount,
        taxAmount: formData.taxAmount,
        totalAmount: calculateTotalAmount(),
        issueDate: formData.issueDate,
        dueDate: formData.dueDate,
        status: formData.status,
        createdBy: user?.userId,
      };

      const result = await dispatch(createInvoice(invoiceData));

      if (createInvoice.fulfilled.match(result)) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/invoices');
        }, 2000);
      }
    } catch (err) {
      console.error('Failed to create invoice:', err);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/invoices')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Invoices
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Create Invoice</h1>
        <p className="mt-1 text-sm text-gray-600">
          Create a new invoice from an existing purchase order
        </p>
      </div>

      {/* Success Message */}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-600">Invoice created successfully! Redirecting...</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
          {/* Purchase Order Selection */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Purchase Order</h2>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Purchase Order <span className="text-red-500">*</span>
                </label>
                <select
                  name="poId"
                  value={formData.poId}
                  onChange={handleFormChange}
                  required
                  disabled={loadingPOs}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select a Purchase Order...</option>
                  {purchaseOrders.map((po: any) => (
                    <option key={po._id} value={po._id}>
                      {po.poNumber} - {po.supplierId?.name} - ${po.totalAmount?.toLocaleString()}
                    </option>
                  ))}
                </select>
                {loadingPOs && <p className="mt-1 text-sm text-gray-500">Loading purchase orders...</p>}
              </div>

              {/* PO Details Preview */}
              {selectedPO && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Purchase Order Details</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">Buyer:</span>
                      <span className="ml-2 text-gray-900">{selectedPO.buyerId?.name}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Supplier:</span>
                      <span className="ml-2 text-gray-900">{selectedPO.supplierId?.name}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">PO Amount:</span>
                      <span className="ml-2 text-gray-900">${selectedPO.totalAmount?.toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Items:</span>
                      <span className="ml-2 text-gray-900">{selectedPO.items?.length || 0}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Invoice Details */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Invoice Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Issue Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="issueDate"
                  value={formData.issueDate}
                  onChange={handleFormChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Due Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="dueDate"
                  value={formData.dueDate}
                  onChange={handleFormChange}
                  required
                  min={formData.issueDate}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleNumberChange}
                  required
                  step="0.01"
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tax Amount
                </label>
                <input
                  type="number"
                  name="taxAmount"
                  value={formData.taxAmount}
                  onChange={handleNumberChange}
                  step="0.01"
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleFormChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="draft">Draft</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                </select>
              </div>
            </div>
          </div>

          {/* Total Amount */}
          <div className="pt-4 border-t border-gray-200">
            <div className="flex justify-end">
              <div className="text-right">
                <p className="text-sm text-gray-500">Total Amount</p>
                <p className="text-2xl font-bold text-gray-900">
                  ${calculateTotalAmount().toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/invoices')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.poId}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Invoice'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreateInvoice;
